import React, { useEffect, useRef } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import CharacterCount from '@tiptap/extension-character-count'
import type { JSONContent } from '@tiptap/core'
import './ArticleEditor.scss'

interface ArticleEditorProps {
  value?: JSONContent | null
  onChange?: (value: JSONContent) => void
  onBlur?: () => void
  error?: string
  name?: string
}

const ArticleEditor: React.FC<ArticleEditorProps> = ({
  value,
  onChange,
  onBlur,
  error,
  name = 'content',
}) => {
  // useEditor captures its callbacks once; refs keep them pointing at the latest props.
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const onBlurRef = useRef(onBlur)
  onBlurRef.current = onBlur

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        horizontalRule: false, // a typed `---` would otherwise be silently lost on save
      }),
      Placeholder.configure({
        placeholder: 'Start writing your article...',
      }),
      CharacterCount,
    ],
    content: value ?? undefined,
    editorProps: {
      attributes: {
        class: 'article-editor__content',
        spellCheck: 'true',
      },
    },
    // No localStorage here: the article form + useBlogRecovery own recovery,
    // keyed per article, so two articles can never see each other's content.
    onUpdate: ({ editor }) => {
      onChangeRef.current?.(editor.getJSON())
    },
    onBlur: () => {
      onBlurRef.current?.()
    },
  })

  // Keep editor in sync when RHF resets / sets defaultValues
  useEffect(() => {
    if (!editor || value === undefined || value === null) return

    const current = JSON.stringify(editor.getJSON())
    const incoming = JSON.stringify(value)

    if (current !== incoming) {
      editor.commands.setContent(value)
    }
  }, [editor, value])

  if (!editor) return null

  const wordCount = editor.storage.characterCount.words()
  const readingTime = Math.max(1, Math.ceil(wordCount / 200))

  return (
    <div className='article-editor'>
      <label className='article-editor__label'>
        Content <span>*</span>
      </label>

      <div className={`article-editor__box ${error ? 'article-editor__box--error' : ''}`}>
        <div className='article-editor__toolbar' role='toolbar'>
          <button
            type='button'
            className={`article-editor__toolbar-btn ${
              editor.isActive('heading') ? 'article-editor__toolbar-btn--active' : ''
            }`}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            Heading
          </button>

          <button
            type='button'
            className={`article-editor__toolbar-btn ${
              editor.isActive('bold') ? 'article-editor__toolbar-btn--active' : ''
            }`}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <strong>B</strong>
          </button>

          <button
            type='button'
            className={`article-editor__toolbar-btn ${
              editor.isActive('italic') ? 'article-editor__toolbar-btn--active' : ''
            }`}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            /
          </button>

          <button
            type='button'
            className={`article-editor__toolbar-btn ${
              editor.isActive('bulletList') ? 'article-editor__toolbar-btn--active' : ''
            }`}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            List
          </button>

          <button
            type='button'
            className={`article-editor__toolbar-btn ${
              editor.isActive('blockquote') ? 'article-editor__toolbar-btn--active' : ''
            }`}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            Quote
          </button>

          <button
            type='button'
            className={`article-editor__toolbar-btn ${
              editor.isActive('paragraph') ? 'article-editor__toolbar-btn--active' : ''
            }`}
            onClick={() => editor.chain().focus().setParagraph().run()}
          >
            Paragraph
          </button>
        </div>

        <EditorContent editor={editor} />
      </div>

      {error && <div className='article-editor__error'>{error}</div>}

      <div className='article-editor__meta'>
        {wordCount} {wordCount === 1 ? 'word' : 'words'} · {readingTime} min read
      </div>
    </div>
  )
}

export default ArticleEditor
